'use client';

import { useEffect, useRef, useState, RefObject } from 'react';

export interface ElementSize {
  width: number;
  height: number;
  x: number;
  y: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export function useElementSize<T extends HTMLElement = HTMLDivElement>(): [
  RefObject<T | null>,
  ElementSize
] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<ElementSize>({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setSize({
        width: rect.width,
        height: rect.height,
        x: rect.x,
        y: rect.y,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
      });
    };

    // Initial measurement
    updateSize();

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(element);

    // Also listen to window resize for position updates
    window.addEventListener('resize', updateSize);
    window.addEventListener('scroll', updateSize, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('scroll', updateSize, true);
    };
  }, []);

  return [ref, size];
}

